import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles, Menu, MenuItem, Divider, Tooltip } from '@material-ui/core'
import Icon from '../../atoms/Icon'

const useStyles = makeStyles(() => ({
  root: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    padding: '5px 10px',
    borderBottom: '1px solid var(--opal-border-default, #eee)',
    background: 'var(--opal-surface-default, #fff)',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: 'var(--color-neutral-600, #666)',
    userSelect: 'none'
  },
  segment: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 6px',
    borderRadius: 'var(--border-radius-sm, 4px)',
    cursor: 'default',
    transition: 'background 150ms ease, color 150ms ease',
    '&:hover': {
      background: 'var(--color-primary-50, rgba(25, 118, 210, 0.08))',
      color: 'var(--color-primary-700, #1565c0)'
    },
    '&:hover $selectHandle': {
      opacity: 1
    }
  },
  segmentHighlighted: {
    background: 'var(--color-primary-50, rgba(25, 118, 210, 0.08))',
    color: 'var(--color-primary-700, #1565c0)'
  },
  // Hover-revealed dot that selects "All <Grouping>" as the chart
  // subject. Same pattern as the row-level select handle in the grid
  // -- hidden until segment hover, filled when the level is active.
  selectHandle: {
    appearance: 'none',
    background: 'transparent',
    border: '1.5px solid var(--color-neutral-400, #aaa)',
    borderRadius: '50%',
    width: 9,
    height: 9,
    padding: 0,
    margin: 0,
    flexShrink: 0,
    cursor: 'pointer',
    opacity: 0,
    transition: 'opacity 120ms ease, background-color 120ms ease, border-color 120ms ease',
    '&:hover': {
      borderColor: 'var(--color-primary-500, #1a73e8)'
    },
    '&:focus-visible': {
      outline: '2px solid var(--color-primary-400, #3a83f5)',
      outlineOffset: 1,
      opacity: 1
    }
  },
  selectHandleActive: {
    opacity: 1,
    background: 'var(--color-primary-500, #1a73e8)',
    borderColor: 'var(--color-primary-500, #1a73e8)',
    '&:hover': {
      background: 'var(--color-primary-600, #1557b0)',
      borderColor: 'var(--color-primary-600, #1557b0)'
    }
  },
  separator: {
    color: 'var(--color-neutral-300, #ccc)',
    fontSize: 10,
    margin: '0 2px'
  },
  menuItem: {
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 32
  },
  exportButton: {
    marginLeft: 'auto',
    padding: '2px 6px',
    borderRadius: 'var(--border-radius-sm, 4px)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'background 150ms ease, color 150ms ease',
    '&:hover': {
      background: 'var(--color-primary-50, rgba(25, 118, 210, 0.08))',
      color: 'var(--color-primary-700, #1565c0)'
    }
  },
  exportMenuHeader: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: 'var(--color-neutral-400, #aaa)',
    padding: '6px 16px 2px',
    cursor: 'default'
  }
}))

export default function GroupingBreadcrumb ({ groupings, onExpandToLevel, onCollapseToLevel, onViewAllocation, onCompareMeasures, highlightedDepth, onExport, selectedLevelDepth = null, onSelectLevel }) {
  const classes = useStyles()
  const [contextMenu, setContextMenu] = useState(null)
  const [exportAnchor, setExportAnchor] = useState(null)

  const handleContextMenu = useCallback((e, segmentIndex) => {
    e.preventDefault()
    setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, segmentIndex })
  }, [])

  const closeMenu = useCallback(() => setContextMenu(null), [])
  const closeExportMenu = useCallback(() => setExportAnchor(null), [])

  const handleExport = useCallback((scope, format) => {
    closeExportMenu()
    if (onExport) onExport({ scope, format })
  }, [onExport, closeExportMenu])

  const segments = groupings

  return (
    <div className={classes.root}>
      {segments.length > 1 && segments.map((label, i) => {
        const levelSelected = onSelectLevel && selectedLevelDepth === i
        return (
          <React.Fragment key={i}>
            {i > 0 && <span className={classes.separator}>&gt;</span>}
            <span
              className={clsx(classes.segment, { [classes.segmentHighlighted]: highlightedDepth === i })}
              onContextMenu={(e) => handleContextMenu(e, i)}
            >
              {onSelectLevel && (
                <Tooltip
                  title={levelSelected ? 'Clear chart subject (Household)' : `Set chart subject: All ${label}`}
                  placement='bottom'
                  enterDelay={400}
                >
                  <button
                    type='button'
                    className={clsx(classes.selectHandle, { [classes.selectHandleActive]: levelSelected })}
                    onClick={(e) => { e.stopPropagation(); onSelectLevel(i, label) }}
                    aria-label={levelSelected ? 'Clear chart subject' : `Set chart subject to all ${label}`}
                  />
                </Tooltip>
              )}
              {label}
            </span>
          </React.Fragment>
        )
      })}

      {onExport && (
        <span
          className={classes.exportButton}
          onClick={(e) => setExportAnchor(e.currentTarget)}
        >
          <Icon name='download' customSize={14} />
        </span>
      )}

      <Menu
        open={contextMenu !== null}
        onClose={closeMenu}
        anchorReference='anchorPosition'
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        {contextMenu !== null && [
          <MenuItem
            key='expand'
            disabled={contextMenu.segmentIndex === 0}
            onClick={() => { onExpandToLevel(contextMenu.segmentIndex); closeMenu() }}
          >
            <span className={classes.menuItem}>
              <Icon name='unfoldMore' customSize={14} />
              Expand to {segments[contextMenu.segmentIndex]}
            </span>
          </MenuItem>,
          <MenuItem
            key='collapse'
            onClick={() => { onCollapseToLevel(contextMenu.segmentIndex); closeMenu() }}
          >
            <span className={classes.menuItem}>
              <Icon name='unfoldLess' customSize={14} />
              Collapse to {segments[contextMenu.segmentIndex]}
            </span>
          </MenuItem>,
          onViewAllocation && (
            <MenuItem
              key='view-allocation'
              onClick={() => {
                onViewAllocation(contextMenu.segmentIndex)
                closeMenu()
              }}
            >
              <span className={classes.menuItem}>
                <Icon name='pieChart' customSize={14} />
                View allocation of {segments[contextMenu.segmentIndex]}
              </span>
            </MenuItem>
          ),
          onCompareMeasures && (
            <MenuItem
              key='compare-measures'
              onClick={() => {
                onCompareMeasures(contextMenu.segmentIndex)
                closeMenu()
              }}
            >
              <span className={classes.menuItem}>
                <Icon name='chart' customSize={14} />
                Compare measures of {segments[contextMenu.segmentIndex]}...
              </span>
            </MenuItem>
          )
        ]}
      </Menu>

      <Menu
        open={Boolean(exportAnchor)}
        anchorEl={exportAnchor}
        onClose={closeExportMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <div className={classes.exportMenuHeader}>Visible rows</div>
        <MenuItem onClick={() => handleExport('visible', 'csv')}>
          <span className={classes.menuItem}>
            <Icon name='download' customSize={14} />
            Download CSV
          </span>
        </MenuItem>
        <MenuItem onClick={() => handleExport('visible', 'clipboard')}>
          <span className={classes.menuItem}>
            <Icon name='copy' customSize={14} />
            Copy to Clipboard
          </span>
        </MenuItem>
        <Divider />
        <div className={classes.exportMenuHeader}>All data</div>
        <MenuItem onClick={() => handleExport('all', 'csv')}>
          <span className={classes.menuItem}>
            <Icon name='download' customSize={14} />
            Download All as CSV
          </span>
        </MenuItem>
        <MenuItem onClick={() => handleExport('all', 'clipboard')}>
          <span className={classes.menuItem}>
            <Icon name='copy' customSize={14} />
            Copy All to Clipboard
          </span>
        </MenuItem>
      </Menu>
    </div>
  )
}

GroupingBreadcrumb.propTypes = {
  groupings: PropTypes.arrayOf(PropTypes.string).isRequired,
  onExpandToLevel: PropTypes.func.isRequired,
  onCollapseToLevel: PropTypes.func.isRequired,
  onViewAllocation: PropTypes.func,
  onCompareMeasures: PropTypes.func,
  highlightedDepth: PropTypes.number,
  onExport: PropTypes.func,
  // When `selectedLevelDepth === i`, segment i's select-handle is filled
  // (blue dot). Click the handle to call `onSelectLevel(depth, label)`.
  selectedLevelDepth: PropTypes.number,
  onSelectLevel: PropTypes.func
}
